import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Time } from '@poseidon/components/StrokeIcons';

import type { SequenceStep } from '~/components/Sequence/sequenceSteps';
import { DEFAULT_WAIT_DAYS, stepLabel } from '~/components/Sequence/sequenceSteps';
import { WaitDurationInput, type WaitUnit } from '~/components/Sequence/WaitDurationInput';

// Stable middleware identity (re-anchors every pan/zoom frame): `flip` opens upward when there's no room
// below, `shift` nudges it to stay on-screen.
const MIDDLEWARE = [offset(6), flip({ padding: 8 }), shift({ padding: 8 })];

interface Props {
  /** The `wait` step. */
  step: SequenceStep;
  /** Shared chip classes (grab cursor, hover shadow, invalid ring) from the node. */
  chipBase: string;
  /** Commit a new wait length + unit. Omit (locked view) → the pill is a static, non-clickable label. */
  onChangeWait?: (days: number, unit: WaitUnit) => void;
}

/**
 * A `wait` step as a compact static pill ("Wait 1 week") that opens a small popover with the editable
 * duration (number + days/weeks) ONLY when clicked — so the graph reads cleanly and the duration editor
 * isn't always taking up space. The popover is **portaled** (like {@link SequenceGraphInsert}) so it
 * escapes every node's stacking context (never painted under an adjacent node), renders at natural size
 * regardless of zoom, and can't be clipped by the canvas. Read-only (no `onChangeWait`) is just the label.
 */
export function SequenceWaitNode({ step, chipBase, onChangeWait }: Props) {
  const [editing, setEditing] = useState(false);
  const { refs, floatingStyles } = useFloating({
    open: editing,
    // Prefer ABOVE the node; `flip` drops it below only when there isn't room above.
    placement: 'top',
    strategy: 'fixed',
    // Track the pill as the canvas is panned/zoomed via CSS transform (which scroll/resize observers miss).
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: MIDDLEWARE,
  });

  const pill = (
    <div
      className={cn(
        'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-lynch-200 bg-white px-3 dark:border-slate-700 dark:bg-slate-900',
        onChangeWait != null && 'cursor-pointer',
        chipBase,
      )}>
      <Time.Clock className="size-4 shrink-0 text-lynch-500 dark:text-lynch-400" />
      <span className="px-0.5 text-xs font-medium text-lynch-600 dark:text-lynch-300">{stepLabel(step)}</span>
    </div>
  );

  if (onChangeWait == null) return pill;

  return (
    <>
      {/* Only OPENS on click — closing is handled by blur-out / Escape. (A toggle here would reopen: the
          same click's blur-out already closes the popover, then a toggle would flip it back on.) */}
      <button
        ref={refs.setReference}
        type="button"
        aria-label="Edit wait duration"
        onClick={() => !editing && setEditing(true)}
        className="block">
        {pill}
      </button>
      {editing && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            data-no-pan
            className="z-50 rounded-lg border border-lynch-200 bg-white p-1.5 shadow-md dark:border-slate-700 dark:bg-slate-900"
            // Commit-then-close: the field commits on its own blur; close only when focus leaves the WHOLE
            // popover (not when moving to the days/weeks select) so an in-flight edit is never dropped.
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
            }}>
            <WaitDurationInput
              autoFocus
              days={step.waitDays ?? DEFAULT_WAIT_DAYS}
              unit={step.waitUnit ?? 'days'}
              onCommit={onChangeWait}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
